/**
 * useShareMutations — W4.8
 *
 * Share Create / Update / Delete via TanStack useMutation.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

// --- Typen ---

export type ShareFormData = {
  hotspot_id: string
  percentage?: number
  fixed_amount?: number
  tag_list?: string
  start_date?: string
  end_date?: string
  payment?: boolean
}

// --- Create Share ---

export function useCreateShare(contactId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: ShareFormData) => {
      const result = await apiClient.POST('/api/v2/contacts/{contact_id}/shares', {
        params: { path: { contact_id: Number(contactId) } },
        // OpenAPI-Contract erwartet den Body gewrapped als { share: {...} }
        // (heliumtracker-web/api-types/openapi.yaml:5074ff) — analog zu allen
        // anderen v2-Mutations-Endpunkten (contacts, commissions, ...).
        body: { share: data } as never, // Using never to satisfy api-types as openapi-fetch types can be strict
      })
      if (result.error) throw result.error
      return result.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contact-detail', contactId] })
      void queryClient.invalidateQueries({ queryKey: ['contacts'] })
      // FIN-CCP-FIX-17: SharesPage (/commissions) und der Payout-Wizard lesen
      // aus separaten Query-Keys ('user-shares' / 'shares') — ohne
      // Invalidierung hier zeigen sie neu angelegte/geaenderte/geloeschte
      // Shares erst nach hartem Reload an.
      void queryClient.invalidateQueries({ queryKey: ['user-shares'] })
      void queryClient.invalidateQueries({ queryKey: ['shares'] })
    },
  })
}

// --- Update Share ---

export function useUpdateShare(contactId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ shareId, data }: { shareId: string; data: Partial<ShareFormData> }) => {
      const result = await apiClient.PATCH('/api/v2/shares/{id}', {
        params: { path: { id: Number(shareId) } },
        body: { share: data } as never, // Using never to satisfy api-types as openapi-fetch types can be strict
      })
      if (result.error) throw result.error
      return result.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contact-detail', contactId] })
      void queryClient.invalidateQueries({ queryKey: ['user-shares'] })
      void queryClient.invalidateQueries({ queryKey: ['shares'] })
    },
  })
}

// --- Delete Share ---

export function useDeleteShare(contactId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (shareId: string) => {
      const result = await apiClient.DELETE('/api/v2/shares/{id}', {
        params: { path: { id: Number(shareId) } },
      })
      if (result.error) throw result.error
      return result.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contact-detail', contactId] })
      void queryClient.invalidateQueries({ queryKey: ['contacts'] })
      void queryClient.invalidateQueries({ queryKey: ['user-shares'] })
      void queryClient.invalidateQueries({ queryKey: ['shares'] })
    },
  })
}
